import { useEffect, useMemo, useState } from 'react';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { BookingList } from '../components/BookingList';
import { Pagination } from '../components/Pagination';
import {
  BookingsFilterBar,
  EMPTY_BOOKINGS_FILTER,
  type BookingsFilterValue,
} from '../components/BookingsFilterBar';
import type { BookingItemProps } from '../components/BookingItem';
import { customerApiErrorMessage } from '../../lib/customer-api';
import {
  listMyBookings,
  listMySubscriptions,
  type BookingSummary,
} from '../../lib/booking-api';
import {
  listMyPatchTests,
  type PatchTestData,
} from '../../lib/messaging-api';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

type Tab =
  | 'Upcoming'
  | 'Pending from pro'
  | 'In progress'
  | 'Completed'
  | 'Past';

// Each tab → the backend status filter to apply. Tabs that span a
// single status hit the backend's `status=` param directly; the
// catch-all Upcoming/Past tabs send only `scope` and let the backend
// split by past/upcoming status buckets.
const TAB_FILTERS: Record<
  Tab,
  { scope?: 'upcoming' | 'past'; status?: string }
> = {
  Upcoming: { scope: 'upcoming' },
  'Pending from pro': { status: 'PENDING_PROFESSIONAL_CONFIRM' },
  'In progress': { status: 'IN_PROGRESS' },
  Completed: { status: 'COMPLETED' },
  Past: { scope: 'past' },
};

const PAGE_SIZE = 20;

const CANCELLED_STATUSES = new Set([
  'CANCELLED_BY_CUSTOMER',
  'CANCELLED_BY_PRO',
  'CANCELLED_BY_ADMIN',
  'REFUNDED',
  'FAILED',
]);

const isCancelled = (s: string) => CANCELLED_STATUSES.has(s);
const isComplete = (s: string) => s === 'COMPLETED';

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const formatDateTime = (iso: string) => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${date} at ${time}`;
  } catch {
    return iso;
  }
};

const formatDuration = (minutes: number): string => {
  if (!minutes || minutes < 1) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const statusLabel = (s: string): string => {
  switch (s) {
    case 'CONFIRMED':
      return 'Booking confirmed';
    case 'PENDING_PAYMENT':
      return 'Awaiting payment';
    case 'IN_PROGRESS':
      return 'In progress';
    case 'COMPLETED':
      return 'Booking completed';
    case 'CANCELLED_BY_CUSTOMER':
      return 'Booking canceled';
    case 'CANCELLED_BY_PRO':
      return 'Cancelled by professional';
    case 'CANCELLED_BY_ADMIN':
      return 'Cancelled by support';
    case 'REFUNDED':
      return 'Refunded';
    case 'FAILED':
      return 'Booking failed';
    default:
      return 'Booking';
  }
};

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// "Booked on …" caption — include the time alongside the date so the
// customer can tell two same-day bookings apart at a glance.
const formatShortDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} at ${time}`;
  } catch {
    return '';
  }
};

const subStatusLabel = (s: string): string => {
  switch (s) {
    case 'ACTIVE':
      return 'Subscription active';
    case 'TRIALING':
      return 'Trial running';
    case 'PAST_DUE':
      return 'Payment past due';
    case 'CANCELLED':
      return 'Subscription cancelled';
    case 'EXPIRED':
      return 'Subscription expired';
    case 'INCOMPLETE':
    case 'INCOMPLETE_EXPIRED':
      return 'Setup incomplete';
    default:
      return 'Subscription';
  }
};

const isLiveSubscription = (s: string) =>
  s === 'ACTIVE' || s === 'TRIALING' || s === 'PAST_DUE' || s === 'INCOMPLETE';

// Subscription parent rows live in `booking_subscriptions`, separate
// from `bookings`. They have no scheduled_start of their own — only a
// recurring cadence — so the list pulls billing date / cadence from
// the subscription itself.
const subToItemProps = (sub: any): BookingItemProps => {
  const provider = (() => {
    const u = sub.preferred_professional?.user;
    const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
    return (
      name || sub.preferred_professional?.business_name || 'A Cherry Picked pro'
    );
  })();
  const live = isLiveSubscription(sub.status);
  const badge: BookingItemProps['badge'] = live
    ? 'Active'
    : sub.status === 'CANCELLED' || sub.status === 'INCOMPLETE_EXPIRED'
      ? 'Canceled'
      : 'Ended';
  const cadence =
    sub.frequency === 'DAILY'
      ? 'Every day'
      : sub.frequency === 'WEEKLY'
        ? 'Every week'
        : sub.frequency === 'FORTNIGHTLY'
          ? 'Every 2 weeks'
          : sub.frequency === 'MONTHLY'
            ? 'Every month'
            : 'Recurring';
  const dayLabel =
    typeof sub.day_of_week === 'number'
      ? ` · ${DAY_NAMES[sub.day_of_week] ?? ''}`
      : '';
  const timeLabel = sub.start_time ? ` at ${String(sub.start_time).slice(0, 5)}` : '';
  const nextBilling = sub.next_billing_at
    ? new Date(sub.next_billing_at).toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : null;
  return {
    id: sub.id,
    status: subStatusLabel(sub.status),
    title: sub.service?.name ?? sub.service_category?.name ?? 'Subscription',
    provider,
    price: formatMoney(
      Number(sub.amount_per_cycle ?? 0),
      sub.currency || 'GBP',
    ),
    frequency: cadence,
    duration: formatDuration(sub.duration_minutes),
    dateTime: `${cadence}${dayLabel}${timeLabel}${
      nextBilling ? ` · Next ${nextBilling}` : ''
    }`,
    bookedOn: sub.created_at ? formatShortDate(sub.created_at) : undefined,
    badge,
    // Real image when we have one; otherwise null → BookingItem
    // renders a brand-neutral placeholder instead of a misleading
    // generic cleaning photo. Prefer the backend's signed preview
    // URL (private bucket) over the canonical key — see
    // Booking.service.signBookingListImages.
    image:
      sub.service?.image_preview_url ||
      sub.service?.image ||
      sub.service_category?.image_url_preview_url ||
      sub.service_category?.image_url ||
      null,
    href: `/subscriptions/${sub.id}`,
  };
};

const toItemProps = (row: BookingSummary): BookingItemProps => {
  const provider = (() => {
    const u = row.professional?.user;
    if (!u) return 'A Cherry Picked pro';
    const name = [u.first_name, u.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || row.professional?.business_name || 'A Cherry Picked pro';
  })();
  const badge: BookingItemProps['badge'] = isCancelled(row.status)
    ? 'Canceled'
    : isComplete(row.status)
      ? 'Ended'
      : 'Active';
  return {
    id: row.id,
    status: statusLabel(row.status),
    title: row.service?.name ?? row.service_category?.name ?? 'Service booking',
    provider,
    price: formatMoney(Number(row.total_amount) || 0, row.currency || 'GBP'),
    frequency: row.source === 'SUBSCRIPTION' ? 'Recurring' : 'Once',
    duration: formatDuration(row.duration_minutes),
    dateTime: formatDateTime(row.scheduled_start),
    bookedOn: row.created_at ? formatShortDate(row.created_at) : undefined,
    badge,
    // Dynamic card image: prefer the backend's signed preview URL
    // (private bucket — see Booking.service.signBookingListImages),
    // then the canonical key as a fallback, then null → BookingItem
    // renders a brand-neutral placeholder. The old "imgService"
    // fallback was a generic cleaning photo that misled customers
    // when their booking was hair/childcare/beauty (e.g. an
    // "Extensions" booking showed someone vacuuming).
    image:
      row.service?.image_preview_url ||
      row.service?.image ||
      row.service_category?.image_url_preview_url ||
      row.service_category?.image_url ||
      null,
  };
};

export default function BookingServicePage() {
  return (
    <RequireCustomer>
      <BookingServicePageInner />
    </RequireCustomer>
  );
}

function BookingServicePageInner() {
  const [activeTab, setActiveTab] = useState<Tab>('Upcoming');
  const [rows, setRows] = useState<BookingSummary[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  // Confirmed patch-test appointments — shown as a separate labelled
  // section above the treatment bookings (kept distinct from them).
  const [patchTests, setPatchTests] = useState<PatchTestData[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<BookingsFilterValue>(
    EMPTY_BOOKINGS_FILTER,
  );

  const tabFilter = TAB_FILTERS[activeTab];

  // Server-paginated — fetch the current page's bookings (limit=10)
  // with the search/filter params applied server-side, plus the
  // customer's subscription parents (a separate, small list).
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const load = async () => {
    setLoading(true);
    try {
      const [bookingsRes, subsRes] = await Promise.all([
        listMyBookings({
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          scope: tabFilter.scope,
          status: tabFilter.status,
          q: filters.q.trim() || undefined,
          service_id: filters.services[0],
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
        }),
        listMySubscriptions({ limit: 100, offset: 0 }),
      ]);
      setRows(bookingsRes.data ?? []);
      setBookingsTotal(bookingsRes.total_count ?? 0);
      setSubscriptions(subsRes.data ?? []);
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not load your bookings.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Confirmed patch tests are fetched once (small list, no pagination) and
  // shown on the Upcoming tab. Best-effort — failure just hides the section.
  useEffect(() => {
    listMyPatchTests('customer')
      .then((res) => setPatchTests(res.data ?? []))
      .catch(() => setPatchTests([]));
  }, []);

  // Reload whenever the tab changes.
  useEffect(() => {
    setPage(1);
  }, [activeTab, filters]);

  // Server hit on tab/page/filter changes — re-fetches the current
  // window with the new params.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, filters]);

  // Filter subscriptions to the active tab — active / trialing / past_due
  // count as "Upcoming"; cancelled / expired collapse into "Past".
  const tabSubscriptions = useMemo(() => {
    return subscriptions.filter((s) => {
      const live = isLiveSubscription(s.status);
      // Only the catch-all "Upcoming" tab shows subscription parents
      // (they have no booking-status concept of their own, so the
      // narrow tabs like Pending / In progress don't apply). Past
      // shows cancelled / ended subs.
      if (activeTab === 'Upcoming') return live;
      if (activeTab === 'Past') return !live;
      return false;
    });
  }, [subscriptions, activeTab]);

  const filteredSubs = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const from = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const to = filters.dateTo ? new Date(filters.dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);
    return tabSubscriptions.filter((s) => {
      if (q) {
        const u = s.preferred_professional?.user;
        const proName = (
          [u?.first_name, u?.last_name].filter(Boolean).join(' ') ||
          s.preferred_professional?.business_name ||
          ''
        ).toLowerCase();
        const svc = (s.service?.name ?? '').toLowerCase();
        const ref = (s.reference ?? '').toLowerCase();
        if (!proName.includes(q) && !svc.includes(q) && !ref.includes(q)) {
          return false;
        }
      }
      if (filters.services.length > 0) {
        const sid = s.service?.id ?? '';
        if (!sid || !filters.services.includes(sid)) return false;
      }
      // Date range is matched against the next billing date when the
      // sub is live, otherwise the cancellation / created date — so a
      // filter still narrows visibly.
      const anchor = s.next_billing_at ?? s.cancelled_at ?? s.created_at;
      if (anchor) {
        const t = new Date(anchor).getTime();
        if (from && t < from.getTime()) return false;
        if (to && t > to.getTime()) return false;
      }
      return true;
    });
  }, [tabSubscriptions, filters]);

  const serviceOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.service?.id && r.service.name && !seen.has(r.service.id)) {
        seen.set(r.service.id, r.service.name);
      }
    }
    for (const s of subscriptions) {
      if (s.service?.id && s.service.name && !seen.has(s.service.id)) {
        seen.set(s.service.id, s.service.name);
      }
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [rows, subscriptions]);

  // The bookings list is server-paginated (this page = `rows`). We
  // only merge subscriptions on PAGE 1 so they don't get duplicated
  // across pages — and we sort by created_at so freshly-booked items
  // appear first.
  const combined = useMemo<BookingItemProps[]>(() => {
    const items: Array<{ when: number; item: BookingItemProps }> = [];
    for (const r of rows) {
      items.push({
        when: r.created_at ? new Date(r.created_at).getTime() || 0 : 0,
        item: toItemProps(r),
      });
    }
    if (page === 1) {
      for (const s of filteredSubs) {
        items.push({
          when: s.created_at ? new Date(s.created_at).getTime() || 0 : 0,
          item: subToItemProps(s),
        });
      }
    }
    items.sort((a, b) => b.when - a.when);
    return items.map((x) => x.item);
  }, [rows, filteredSubs, page]);

  // total = server-side bookings count + (page 1 only) the live
  // subscriptions shown alongside it. Past tabs don't add subs since
  // those collapse into cancelled/ended buckets handled by the
  // tabSubscriptions filter.
  const total =
    bookingsTotal + (page === 1 ? filteredSubs.length : 0);
  const totalPages = Math.max(1, Math.ceil(bookingsTotal / PAGE_SIZE));
  const visible = useMemo(
    () => combined.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [combined, page],
  );

  const switchTab = (tab: Tab) => {
    if (tab === activeTab) return;
    setPage(1);
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-[#fff] flex flex-col">
      <MainHeader />

      <main className="flex-1 w-full max-w-[1140px] mx-auto px-4 py-4">
        <div className="flex flex-col gap-4 mb-6">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[36px]">
            Your Bookings
          </h1>

          <div className="bg-white border border-[#eee] rounded-lg p-1 flex flex-nowrap overflow-x-auto scrollbar-hide">
            {(
              [
                'Upcoming',
                'Pending from pro',
                'In progress',
                'Completed',
                'Past',
              ] as Tab[]
            ).map((tab) => (
              <button
                key={tab}
                onClick={() => switchTab(tab)}
                className={`px-4 sm:px-6 h-[44px] rounded-md font-['DM_Sans',sans-serif] font-bold text-[13px] sm:text-[14px] transition-all flex items-center justify-center whitespace-nowrap shrink-0 ${
                  activeTab === tab
                    ? 'bg-[#fcfaf7] text-[#3a3335] shadow-sm'
                    : 'text-[#908e8e] hover:text-[#3a3335]'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <BookingsFilterBar
          value={filters}
          onChange={setFilters}
          serviceOptions={serviceOptions}
          searchPlaceholder="Search by service, professional or reference…"
        />

        {/* Patch-test appointments — a separate labelled section above the
            treatment bookings (kept distinct from them). Upcoming tab only. */}
        {activeTab === 'Upcoming' && patchTests.length > 0 ? (
          <div className="mb-6">
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] mb-3">
              Patch test appointments
            </p>
            <div className="space-y-2">
              {patchTests.map((pt) => (
                <div
                  key={pt.id}
                  className="flex items-center gap-3 rounded-[12px] border border-[#f3d6a0] bg-[#fff7e6] px-4 py-3"
                >
                  <span className="material-icons text-[#8e2a2f] text-[22px]">
                    event_available
                  </span>
                  <div className="min-w-0">
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                      Patch test with {pt.professional_name}
                    </p>
                    <p className="text-[12px] text-[#908e8e]">
                      {new Date(pt.scheduled_start).toLocaleString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {` · ${pt.duration_minutes} min`}
                      {pt.service_name ? ` · before your ${pt.service_name}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
            Loading your bookings…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-icons text-[48px] text-[#eee] mb-3">
              event_busy
            </span>
            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
              {rows.length === 0 && tabSubscriptions.length === 0
                ? `No ${activeTab.toLowerCase()} bookings`
                : 'No bookings match your search or filters'}
            </p>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              {rows.length === 0 && tabSubscriptions.length === 0
                ? activeTab === 'Upcoming'
                  ? 'Your upcoming appointments will appear here once booked.'
                  : 'Past appointments will appear here.'
                : 'Try removing some filters or clearing the search.'}
            </p>
          </div>
        ) : (
          <>
            <BookingList bookings={visible} />
            {totalPages > 1 ? (
              <div className="flex justify-center mt-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            ) : null}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
